/**
 * Внутренние комментарии команды к заказу (order-internal-notes; ТЗ 26.10.2026, п.9.5: «возможность
 * оставить комментарий к заказу, клиенту его не видно, только нам»). Лента от старых к новым и
 * поле нового комментария; видят те, кому виден заказ.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { teamApi, type OrderInternalNote } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Alert } from '@/design-system/components/feedback/alert'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Hint } from '@/design-system/components/primitives/text'

const MAX_LEN = 4000

/** Стили текстового поля — те же токены, что у Input (как у «Вернуть клиенту на доработку»). */
const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function OrderInternalNotes({
  orderId,
  className,
}: {
  orderId: string
  className?: string
}) {
  const [items, setItems] = useState<OrderInternalNote[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Начальная загрузка: её ответ не должен перезаписать ленту после добавления комментария.
  const loadController = useRef<AbortController | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadController.current = controller
    setItems(null)
    setLoadError(null)
    setBody('')
    setError(null)
    teamApi.orders
      .listNotes(orderId, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setItems(res.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setLoadError(getErrorMessage(err, 'Не удалось загрузить комментарии'))
        }
      })
    return () => controller.abort()
  }, [orderId])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) {
      setError('Напишите комментарий')
      return
    }
    setError(null)
    setSaving(true)
    try {
      const res = await teamApi.orders.addNote(orderId, body)
      loadController.current?.abort()
      setItems(res.items)
      setLoadError(null)
      setBody('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось добавить комментарий'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card header="Внутренние комментарии" className={className}>
      <Hint className="mb-3">Видны только команде Центрики — клиенту не показываются.</Hint>
      {loadError ? (
        <Alert variant="danger" className="mb-3">
          {loadError}
        </Alert>
      ) : items === null ? (
        <p className="mb-3 text-txt-secondary">Загрузка…</p>
      ) : items.length === 0 ? (
        <p className="mb-3 text-txt-secondary">Комментариев пока нет.</p>
      ) : (
        <ul className="mb-4 flex flex-col gap-3" aria-label="Комментарии к заказу">
          {items.map((n) => (
            <li key={n.id} className="rounded-12 border border-line px-3 py-2">
              <div className="text-12 text-txt-secondary">
                {n.authorName || 'Удалённая учётная запись'} · {formatDateTime(n.createdAt)}
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-txt">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} noValidate>
        <Field label="Новый комментарий" error={error ?? undefined} className="my-0">
          {(p) => (
            <textarea
              {...p}
              className={TEXTAREA_CLASS}
              value={body}
              maxLength={MAX_LEN}
              rows={3}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Например: клиент просил счёт на другое юрлицо"
            />
          )}
        </Field>
        <Button type="submit" className="mt-3" disabled={saving}>
          {saving ? 'Сохранение…' : 'Добавить комментарий'}
        </Button>
      </form>
    </Card>
  )
}
